import styled, { css } from 'styled-components';
import { Section, Content, Title as SectionTitle, groupedSectionCornersRow } from 'ui-kit/Section/styles';
import { ButtonWrapper } from 'ui-kit/Button/styles';
import { deviceLess } from '_constants/DEVICES';
import { DEVICES } from '_constants';

export const Page = styled.span`
  color: var(--dim-grey);
`;

export const Wrapper = styled.div`
  height: 100%;
  padding: 40px 25px 50px;
  @media ${deviceLess.desktopHd} {
    display: flex;
    flex-direction: column;
    max-width: 1920px;
    margin: auto;
  }

  > ${Section} {
    ${ButtonWrapper} {
      position: absolute;
      top: 25px;
      right: 35px;
      padding: 0 20px;
    }
  }
`;
export const InputWrapper = styled.div`
  margin-bottom: 25px;

  input {
    max-width: 600px;
    width: 100%;
  }

  .react-time-picker {
    max-width: 600px;
  }

  .BaseSelect__control {
    max-width: 600px;
  }
`;

export const Title = styled.label`
  display: inline-block;
  margin: 0 0 5px;
  background: var(--white);
  font-size: 14px;
  line-height: 22px;
  color: var(--dark);
  user-select: none;
  @media ${deviceLess.laptop} {
    position: absolute;
  }
`;

export const Dl = styled.dl`
  padding: 5px 20px;
  overflow: hidden;
  user-select: text;
  ${({ isSubmission }) =>
    isSubmission &&
    css`
      padding: 10px 20px !important;
      overflow: visible;
    `}
`;

export const RowWrapper = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: stretch;
  background: var(--white);
  border: 1px solid var(--grey);
  border-radius: var(--panel-radius);
  @media ${DEVICES.tablet} {
    flex-direction: column;
  }

  ${Section} {
    position: relative;
    height: fit-content;
    user-select: text;
    @media ${deviceLess.tablet} {
      width: 30%;
      min-width: 350px;
    }
    border: none;

    /* Closes the dark header band along the bottom, matching the vertical divider between the two
       panels (border-right below, same 1px var(--grey)). ui-kit/Section already draws this rule but
       --panel-head-border is transparent in the new design, so only the colour is needed here --
       scoped to these grouped panels rather than changed in the token, which every section header
       in the app reads. */
    ${SectionTitle} {
      border-bottom-color: var(--grey);
    }

    ${Dl} {
      ${({ isSubmission }) =>
        isSubmission &&
        css`
          padding: 10px 20px;
        `}
      padding: 5px 20px;
    }

    ${Content} {
      padding: 0;
    }
    :first-of-type {
      @media ${deviceLess.tablet} {
        width: 70%;
      }
      border-right: 1px solid var(--grey);

      ${InputWrapper} {
        padding: 25px 20px;
        margin: 0;

        :not(:last-of-type) {
          border-bottom: 1px solid var(--lite-grey);
        }
      }
    }
  }

  /* Several sections in one card: only the outer corners round — first on the left, last on the
     right — so the seams stay square. */
  ${groupedSectionCornersRow}
`;

export const Dt = styled.dt`
  margin: 0 0 10px;
  padding: 0;
  font-size: var(--detail-label-size);
  line-height: 1.17;
  color: var(--dim-grey);
  user-select: text;
`;

export const Dd = styled.dd`
  font-size: var(--detail-value-size);
  font-weight: var(--detail-value-weight);
  line-height: 1.33;
  color: var(--text-strong);
  user-select: text;

  :not(:last-of-type) {
    margin-bottom: 25px;
  }

  a {
    color: var(--link-color);
    text-decoration: underline;

    &:hover {
      text-decoration: none;
    }
  }

  img {
    display: block;
    width: auto;
    max-height: 206px;
    height: 100%;
    object-fit: contain;
    max-width: 100%;
    cursor: pointer;
  }
`;

export const ItemWrapper = styled.div`
  margin-bottom: 20px;
  position: relative;
  user-select: text;
  @media ${deviceLess.laptop} {
    display: flex;
    flex-direction: row;
    padding: 5px 0;
    margin-bottom: 10px;

    dt {
      width: 100%;
      min-width: 200px;
      max-width: 50%;
      display: inline-block;
      word-wrap: break-word;
    }
    > dd {
      padding-left: 25px;
      width: 100%;
      min-width: 200px;
      display: inline-block;
      word-wrap: break-word;
    }
  }
`;

export const VerticalBorder = styled.div`
  @media ${deviceLess.laptop} {
    border-right: 1px solid #e0e0e0;
    position: absolute;
    width: 50%;
    min-width: 200px;
    max-width: 50%;
    height: 100%;
    z-index: 1;
    pointer-events: none;
  }
`;

export const HorizontalBorder = styled.div`
  border-bottom: 1px solid #e0e0e0;
  position: absolute;
  top: ${({ subTable }) => (subTable ? '102%' : '110%')};
  left: -30px;
  width: 200%;
  z-index: 1;
  filter: opacity(0.4);
  pointer-events: none;

  @media ${deviceLess.laptop} {
    height: 100%;
    top: 0;
    left: -30px;
    width: 200%;
    z-index: 1;
    filter: opacity(0.4);
  }
`;

export const SubTitle = styled.h3`
  width: calc(100% + 40px);
  transform: translateX(-20px);
  margin: 25px 0 35px;
  padding: 5px 20px 0;
  font-size: 16px;
  border-top: 1px solid var(--grey);
`;

export const ViolationButton = styled.div`
  position: absolute;
  right: 20px;
  top: 2px;
  z-index: 3;
`;

export const ViolationSectionGroup = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: stretch;
  border: 1px solid var(--grey);
  border-radius: var(--panel-radius);
  overflow: hidden;

  /**
   * On the visit detail page this group sits directly under <RowWrapper /> (VisitViewDetails
   * renders it right after, and <AccessControl /> returns its children with no wrapper element, so
   * the two really are adjacent siblings). Two bordered, rounded cards stacked like that met in a
   * seam: a doubled border plus four curves seen edge-on.
   *
   * They read as one continuous card instead -- the facing corners square and the line between
   * them goes. The dark header immediately below the panel above is separation enough.
   */
  ${RowWrapper} + & {
    border-top: none;
    border-top-left-radius: 0;
    border-top-right-radius: 0;

    /* The panels and their filled headers square off too -- groupedSectionCornersRow below rounds
       the outermost of each, which is right for a card that starts here but wrong for one joined to
       the panel above. Corners are rounded only where nothing sits above.
       These carry the sibling selector as well, so they outrank that mixin (which is interpolated
       further down and would otherwise win the tie on source order). */
    > section:first-child,
    > section:last-child {
      border-top-left-radius: 0;
      border-top-right-radius: 0;
    }

    > section:first-child > ${SectionTitle}, > section:last-child > ${SectionTitle} {
      border-top-left-radius: 0;
      border-top-right-radius: 0;
    }
  }

  /* The other half of that join. :has() because only the DOM knows whether this group rendered --
     it is behind a data check AND a permission gate, so the panel above cannot be told in advance.
     Where :has() is unsupported the seam simply stays as it was; nothing breaks. */
  ${RowWrapper}:has(+ &) {
    border-bottom: none;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }

  @media ${DEVICES.tablet} {
    flex-direction: column;
  }

  ${Section} {
    position: relative;
    height: inherit;
    min-width: 338px;
    border: none;
    overflow: hidden;

    /* Same header rule as RowWrapper above: this group draws the identical vertical divider, so
       its headers close the same way. */
    ${SectionTitle} {
      border-bottom-color: var(--grey);
    }

    @media ${deviceLess.tablet} {
      width: 30%;
      min-width: 350px;
    }

    ${Dl} {
      ${({ isSubmission }) =>
        isSubmission &&
        css`
          padding: 10px 20px;
        `}
      padding: 5px 20px;
    }

    ${Content} {
      padding: 0;
    }

    :first-of-type {
      width: 100%;

      @media ${deviceLess.tablet} {
        width: 70%;
      }
      border-right: 1px solid var(--grey);
    }
  }

  /* Several sections in one card: only the outer corners round — first on the left, last on the
     right — so the seams stay square. */
  ${groupedSectionCornersRow}
`;

export const SubmissionItemWrapper = styled.div`
  margin-bottom: 10px;
  user-select: text;
  > dt {
    margin: 0;
  }

  > dd {
    min-width: 298px;
    display: inline-block;
    word-wrap: break-word;
  }

  @media ${deviceLess.laptop} {
    display: flex;
    flex-direction: row;

    dt {
      width: 170px;
      min-width: 130px;
    }
    > dd {
      padding-left: 10px;
      width: 100%;
      min-width: 200px;
      display: inline-block;
      word-wrap: break-word;
    }
  }
`;

export const SubTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: 0 0 16px;
  font-size: 15px;
  line-height: 1.4;
  color: #333;
  background-color: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  user-select: text;

  th,
  td {
    padding: 12px 16px;
    text-align: left;
  }

  th {
    background-color: #f9fafb;
    color: #555;
    font-weight: 600;
    width: 30%;
    border-right: 1px solid #eee;
  }

  td {
    color: #333;
  }

  tr + tr {
    border-top: 1px solid #f0f0f0;
  }
`;

export const InspectorItemWrapper = styled(ItemWrapper)`
  cursor: pointer;
  transition: background-color 0.2s ease;
  padding: 10px;
  margin: -10px;
  margin-bottom: 10px;
  border-radius: 4px;

  &:hover {
    background-color: #f3f4f6;
  }
`;

export const InspectorMetadata = styled.div`
  font-size: 14px;
  color: #6b7280;
  margin-top: 8px;
  font-family: monospace;
  line-height: 1.8;
  font-weight: 700;
`;

export const InspectorModeButtonWrapper = styled.div`
  position: relative;
  bottom: 20px;
  width: 300px;
`;

export const TableTitle = styled.h2`
  position: relative;
  width: calc(100% + 40px);
  right: 20px;
  bottom: 10px;
  z-index: 2;
  border-top: 1px solid var(--panel-head-border);
  border-bottom: 1px solid var(--panel-head-border);
  height: 50px;
  /* The same scheme as the <Section /> header above it — see --panel-head-* in base.scss. The
     redesign's dark fill is its own separator, so the rules above collapse to transparent. */
  background: var(--panel-head-bg);
  color: var(--panel-head-color);
  font-size: 14px;
  font-weight: var(--panel-head-weight);
  line-height: 50px;
  padding: 0px 20px;
`;

/* New design: heading above the active section's content, mirroring the other detail views. */
export const SectionHeading = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
  color: var(--forest);

  h2 {
    font-size: 20px;
    font-weight: 700;
    margin: 0;
  }
`;
